import {Component, DestroyRef, ElementRef, inject, OnInit, viewChild} from '@angular/core';
import {forkJoin, Observable, of, switchMap} from 'rxjs';
import {TableLayoutHttpService} from '../data-access/table-layout-http.service';
import {
  AreaNode,
  BACKGROUND_ACCEPT,
  DEFAULT_TABLE_SIZE,
  EDITOR_ACCENT,
  FloorArea,
  FloorPlanBackground,
  FloorTable,
  isDraftId,
  MIN_AREA_NODES,
  NEW_AREA_COLOR,
  newDraftId,
  nextTableName,
  TableShape,
  TableStatus,
  toAreaUpdate,
  toTableUpdate,
  validateBackgroundFile
} from '../shared/models/table-layout.model';
import {
  areaOfTable,
  backgroundHeight,
  backgroundWidthOrNatural,
  polygonCentroid,
  polygonLabelAnchor,
  rebaseOrigin,
  withAlpha
} from '../shared/models/floor-plan-geometry';
import {FloorPlanCanvasComponent, FloorPlanMode, RenderedBackground} from './floor-plan-canvas.component';
import {TableEditorComponent} from './table-editor.component';
import {AreaEditorComponent} from './area-editor.component';
import {BackgroundEditorComponent} from './background-editor.component';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';

/** The minor grid spacings the switcher offers, in centimetres (the handover's §3). */
const GRID_STEPS: readonly {cm: number; label: string}[] = [
  {cm: 10, label: '10 cm'},
  {cm: 20, label: '20 cm'},
  {cm: 50, label: '50 cm'},
  {cm: 100, label: '1 m'}
];

/** How far a duplicate is offset from its original, in centimetres — the handover's own nudge. */
const DUPLICATE_OFFSET = 40;

/** What the inspector is currently describing. */
type Selection = 'table' | 'area' | 'background' | null;

/**
 * "Tischplan" — the admin screen behind `/company/table-layout` (#201).
 *
 * It manages a shard's **persistent** table catalog, the polygon areas tables are grouped into, and the background
 * image the layout is traced over: place, size, shape and name tables on an infinite metric floor plan, draw and
 * reshape areas, colour them, and move the plan's origin. Ad-hoc (`temporary`) tables are a different, untouched
 * flow — everything written here is persistent by construction (see `toTableUpdate`).
 *
 * **This is a migration of a design handover, not a page built around it.** The layout, spacing, colours and chrome
 * are `Tischplan Editor.dc.html`'s, copied literally: a full-bleed editor (a top bar and then nothing but canvas)
 * where *every* panel — coordinate readout, banner, add-table bar, zoom cluster, scale bar, hint, inspector — floats
 * over the plan. There is no card, no side column and no page scroll, and the accent is the handover's
 * {@link EDITOR_ACCENT} rather than this app's `theme-primary-600`. Anything here that looks like an odd number
 * (`13.5px`, `#f4f2ef`, `0 16px 44px -14px`) is the handover's own value and must stay one.
 *
 * **The host owns the data, the canvas owns the camera.** Every gesture the canvas emits lands in this component's
 * draft; the canvas keeps only pan/zoom/cursor state. That split is also what makes the live table→area match work:
 * because a drag emits a move per pixel, {@link resolveTableAreas} re-resolves the affected table's `areaId` on
 * every one of them, so the inspector's *Bereich* row and the id that eventually gets saved are the same value.
 *
 * **Draft-then-save.** Dragging a marker must not fire an HTTP request per pixel, so every edit lands in a local
 * draft and "Speichern" flushes it: pending deletes first, then the areas (whose freshly minted ids are mapped onto
 * the tables that point at them), then the tables — each set in one batched `UpsertList` commit — and finally the
 * background's placement. This is the one place the migration adds behaviour the handover only stubbed: its own
 * Speichern button was a visual placeholder.
 *
 * **The background image is not part of the draft.** Its bytes live in blob storage and its placement is a single
 * row, so moves/resizes are held locally and pushed with the same Speichern; the *file* itself uploads immediately
 * (there is nothing to batch it with).
 *
 * Backed by `/booking/Table/*`, `/booking/Area/*` and `/booking/FloorPlan/*`.
 * See docs/articles/booking/table-selection-and-areas.md.
 */
@Component({
  selector: 'app-table-layout',
  standalone: true,
  imports: [FloorPlanCanvasComponent, TableEditorComponent, AreaEditorComponent, BackgroundEditorComponent],
  styleUrl: './floor-plan-editor.scss',
  styles: [`
    /* Fills the whole routed area (the shell already reserves the space under the navbar) — this app's
       equivalent of the handover's fixed, inset-0 root. */
    .tp-root {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      background: #e7e5e1;
      color: #1c1c1e;
      font-family: 'Rubik', system-ui, sans-serif;
      -webkit-font-smoothing: antialiased;
      overflow: hidden;
    }

    /* ---- top bar ---- */

    .tp-bar {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      gap: 12px 16px;
      flex-wrap: wrap;
      padding: 11px 16px;
      background: #fff;
      border-bottom: 1px solid #e2e0dc;
      z-index: 30;
    }

    .tp-brand {
      display: flex;
      align-items: center;
      gap: 11px;
    }

    .tp-brand-mark {
      width: 34px;
      height: 34px;
      border-radius: 10px;
      background: #c50e6c;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
    }

    .tp-brand-title {
      font-size: 16px;
      font-weight: 700;
      line-height: 1.1;
    }

    .tp-brand-sub {
      font-size: 12.5px;
      /* a11y fix (#205, out of this screen's own scope but shared with it): the handover's #8a8a8e is 3.4:1 on
         white and fails WCAG AA for body text — axe flags it as a serious violation on the Manager/Furnished
         story. #6b6b70 is the SAME one-step-darker grey the Offene-Tische screen already adopted for its own
         copy of this sub-headline (see OpenTablesComponent's .ot-brand-sub and
         docs/articles/booking/open-tables-overview.md §8.5), so the two floor-plan screens now share one
         accessible value instead of disagreeing. The shift is imperceptible side by side. */
      color: #6b6b70;
      line-height: 1.1;
      margin-top: 1px;
    }

    .tp-group {
      display: flex;
      align-items: center;
      gap: 4px;
      background: #f4f2ef;
      border-radius: 12px;
      padding: 4px;
    }

    .tp-tool {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 8px 13px;
      border-radius: 9px;
      font-size: 13.5px;
      font-weight: 600;
      background: transparent;
      /* #6b6b70, not the handover's #8a8a8e: on this tool's own .tp-group #f4f2ef surface that grey is 3.08:1
         and fails WCAG AA; this one clears it at 4.74:1. Matches .tp-brand-sub / .tp-note — one accessible
         secondary grey across the module (#205). */
      color: #6b6b70;
    }

    .tp-tool.is-active {
      background: #fff;
      color: #c50e6c;
      box-shadow: 0 1px 4px rgba(0, 0, 0, .1);
    }

    .tp-chips {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .tp-chip {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 8px 14px;
      border-radius: 999px;
      font-size: 13.5px;
      font-weight: 600;
      background: #f4f2ef;
      /* #6b6b70, not the handover's #8a8a8e: on this chip's own #f4f2ef surface that grey is 3.08:1 and fails
         WCAG AA; this one clears it at 4.74:1. Matches .tp-brand-sub / .tp-note (#205). */
      color: #6b6b70;
      box-shadow: inset 0 0 0 1px #e7e5e1;
      white-space: nowrap;
    }

    .tp-chip.is-active {
      background: #fdf1f7;
      color: #c50e6c;
      box-shadow: inset 0 0 0 1.5px #c50e6c;
    }

    .tp-minor-group {
      display: flex;
      align-items: center;
      gap: 3px;
      background: #f4f2ef;
      border-radius: 10px;
      padding: 3px;
    }

    .tp-minor {
      display: inline-flex;
      align-items: center;
      padding: 6px 11px;
      border-radius: 8px;
      font-size: 12.5px;
      font-weight: 600;
      background: transparent;
      /* #6b6b70, not the handover's #8a8a8e: on this button's own .tp-minor-group #f4f2ef surface that grey is
         3.08:1 and fails WCAG AA; this one clears it at 4.74:1. Matches .tp-brand-sub / .tp-note (#205). */
      color: #6b6b70;
      white-space: nowrap;
    }

    .tp-minor.is-active {
      background: #fff;
      color: #c50e6c;
      box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
    }

    .tp-bar-spacer {
      flex: 1 1 20px;
    }

    .tp-actions {
      display: flex;
      align-items: center;
      gap: 9px;
    }

    .tp-save {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 9px 18px;
      border-radius: 999px;
      background: #c50e6c;
      color: #fff;
      font-weight: 600;
      font-size: 14px;
      white-space: nowrap;
    }

    .tp-save:disabled {
      opacity: .5;
      cursor: not-allowed;
    }

    /* ---- stage ---- */

    .tp-stage {
      position: relative;
      flex: 1;
      min-height: 0;
      overflow: hidden;
      background: #e9e7e3;
    }

    /* ---- add-table bar ---- */

    .tp-addbar {
      position: absolute;
      left: 50%;
      bottom: 18px;
      transform: translateX(-50%);
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px;
      border-radius: 18px;
      background: #fff;
      box-shadow: 0 10px 30px -10px rgba(0, 0, 0, .28);
      border: 1px solid #eceae7;
      z-index: 10;
    }

    .tp-add {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 9px 16px;
      border-radius: 12px;
      background: #f6f5f3;
      min-width: 74px;
      color: #3d3d40;
    }

    .tp-add span {
      font-size: 11.5px;
      font-weight: 600;
      color: #4b4b50;
    }

    /* ---- inspector ---- */

    /* right: 76px, not the handover's 14px — it steps aside by the relocated zoom cluster's 46 + 16 + a 14 px
       gutter (see .fpc-zoom in the canvas). Everything else about the panel is the handover's. */
    .tp-inspector {
      position: absolute;
      top: 14px;
      right: 76px;
      width: 290px;
      max-width: calc(100vw - 90px);
      max-height: calc(100% - 28px);
      overflow-y: auto;
      background: #fff;
      border-radius: 18px;
      box-shadow: 0 16px 44px -14px rgba(0, 0, 0, .32);
      border: 1px solid #eceae7;
      z-index: 14;
    }

    .tp-inspector-head {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px 18px 14px;
      border-bottom: 1px solid #f2f1ee;
    }

    .tp-inspector-icon {
      width: 34px;
      height: 34px;
      flex: 0 0 auto;
    }

    .tp-inspector-titles {
      flex: 1;
      min-width: 0;
    }

    .tp-kind {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .06em;
      text-transform: uppercase;
      /* #6b6b70, not the handover's #a0a0a4: on the white inspector card that grey is 2.61:1 and fails WCAG AA;
         this one clears it at 5.30:1. Matches .tp-brand-sub / .tp-note (#205). */
      color: #6b6b70;
    }

    .tp-inspector-head .cq-in {
      margin-top: 3px;
      font-size: 16px;
      font-weight: 600;
    }

    .tp-static-name {
      font-size: 19px;
      font-weight: 700;
      margin-top: 3px;
    }

    .tp-close {
      width: 32px;
      height: 32px;
      border-radius: 999px;
      background: #f0efed;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      color: #3d3d40;
    }

    .tp-inspector-body {
      padding: 16px 18px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .tp-inspector-actions {
      display: flex;
      gap: 8px;
      padding-top: 2px;
    }

    .tp-dup,
    .tp-del {
      flex: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      padding: 11px;
      border-radius: 12px;
      font-weight: 600;
      font-size: 14px;
    }

    .tp-dup {
      background: #f0efed;
      color: #3d3d40;
    }

    .tp-del {
      background: #fdecec;
      color: #d64242;
    }

    /* ---- load / error, which the handover has no counterpart for ---- */

    .tp-status {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 20;
      background: rgba(233, 231, 227, .75);
    }

    .tp-status-card {
      background: #fff;
      border-radius: 18px;
      border: 1px solid #eceae7;
      box-shadow: 0 16px 44px -14px rgba(0, 0, 0, .32);
      padding: 20px 24px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      font-size: 13.5px;
      font-weight: 500;
      color: #5c5f66;
    }
  `],
  template: `
    <div class="tp-root">

      <div class="tp-bar">
        <div class="tp-brand">
          <span class="tp-brand-mark" aria-hidden="true">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1.6"/>
              <rect x="14" y="3" width="7" height="7" rx="3.5"/>
              <rect x="3" y="14" width="7" height="7" rx="3.5"/>
              <rect x="14" y="14" width="7" height="7" rx="1.6"/>
            </svg>
          </span>
          <div>
            <h1 class="tp-brand-title">Tischplan</h1>
            <div class="tp-brand-sub">Layout bearbeiten</div>
          </div>
        </div>

        <div class="tp-group" role="group" aria-label="Werkzeug">
          @for (tool of tools; track tool.mode) {
            <button type="button" class="cq-btn tp-tool" [class.is-active]="mode === tool.mode"
                    [attr.aria-pressed]="mode === tool.mode" (click)="setMode(tool.mode)">
              @switch (tool.mode) {
                @case ('select') {
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M5 3l14 7-6 2-2 6z"/>
                  </svg>
                }
                @case ('origin') {
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="3.2"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
                  </svg>
                }
                @default {
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M12 3l8 5-3 10H7L4 8z"/>
                  </svg>
                }
              }
              {{ tool.label }}
            </button>
          }
        </div>

        <div class="tp-chips">
          <button type="button" class="cq-btn tp-chip" [class.is-active]="gridVisible"
                  [attr.aria-pressed]="gridVisible" (click)="gridVisible = !gridVisible">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" aria-hidden="true"><path d="M3 9h18M3 15h18M9 3v18M15 3v18"/></svg>
            Raster
          </button>
          <button type="button" class="cq-btn tp-chip" [class.is-active]="snapEnabled"
                  [attr.aria-pressed]="snapEnabled" (click)="snapEnabled = !snapEnabled">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M5 5l6 6M4 9V4h5M19 19l-6-6M20 15v5h-5"/>
            </svg>
            Einrasten
          </button>

          <!-- Minor grid spacing. The thick 1 m lines are drawn regardless of this choice. -->
          <div class="tp-minor-group" role="group" aria-label="Rasterweite">
            @for (step of gridSteps; track step.cm) {
              <button type="button" class="cq-btn tp-minor" [class.is-active]="minorGridCm === step.cm"
                      [attr.aria-pressed]="minorGridCm === step.cm"
                      (click)="minorGridCm = step.cm">{{ step.label }}</button>
            }
          </div>
        </div>

        <div class="tp-bar-spacer"></div>

        <div class="tp-actions">
          <button type="button" class="cq-btn tp-chip" [class.is-active]="backgroundSelected"
                  [attr.aria-pressed]="backgroundSelected" (click)="onBackgroundChip()" [disabled]="uploadingBackground">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="4" width="18" height="16" rx="2.5"/>
              <circle cx="8.5" cy="9.5" r="1.6"/>
              <path d="M4 18l5-5 3 3 4-4 4 4"/>
            </svg>
            {{ background ? 'Hintergrund' : 'Bild' }}
          </button>
          <button type="button" class="cq-btn tp-save" (click)="save()" [disabled]="saving">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.2"
                 stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-11"/></svg>
            Speichern
          </button>
        </div>
      </div>

      <!-- Hidden by design: the visible controls are the labelled "Bild" chip and "Bild ersetzen", so the input
           needs no label of its own — but it must stay in the DOM for the click to reach it. -->
      <input #file type="file" [attr.accept]="accept" hidden tabindex="-1" aria-hidden="true"
             (change)="onFilePicked($event)"/>

      <div class="tp-stage">
        <app-floor-plan-canvas #canvas
                               [tables]="tables" [areas]="areas" [background]="renderedBackground"
                               [mode]="mode"
                               [selectedTableId]="selectedTableId" [selectedAreaId]="selectedAreaId"
                               [backgroundSelected]="backgroundSelected"
                               [draftNodes]="draftNodes"
                               [minorGridCm]="minorGridCm" [gridVisible]="gridVisible"
                               [snapEnabled]="snapEnabled"
                               (tableSelected)="selectTable($event)"
                               (areaSelected)="selectArea($event)"
                               (selectionCleared)="clearSelection()"
                               (tableMoved)="moveTable($event)"
                               (tableResized)="resizeTable($event)"
                               (areaMoved)="moveArea($event)"
                               (areaNodeMoved)="moveAreaNode($event)"
                               (areaNodeAdded)="addAreaNode($event)"
                               (areaNodeRemoved)="removeAreaNode($event)"
                               (canvasPointed)="addDraftNode($event)"
                               (draftClosed)="finishArea()"
                               (draftCancelled)="cancelArea()"
                               (originPicked)="moveOrigin($event)"
                               (backgroundMoved)="moveBackground($event)"
                               (backgroundResized)="resizeBackground($event)"
                               (deleteRequested)="deleteSelection()"/>

        <!-- Add-table bar, floating over the plan at the bottom centre (the handover's §4). -->
        <div class="tp-addbar">
          @for (shape of shapes; track shape.value) {
            <button type="button" class="cq-btn tp-add" (click)="addTable(shape.value)"
                    [attr.aria-label]="shape.label + ' hinzufügen'">
              @if (shape.value === TableShape.Circle) {
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                     aria-hidden="true"><circle cx="12" cy="12" r="6.5"/></svg>
              } @else {
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                     aria-hidden="true"><rect x="5.5" y="5.5" width="13" height="13" rx="2.5"/></svg>
              }
              <span>{{ shape.label }}</span>
            </button>
          }
        </div>

        <!-- One inspector whose body swaps with the selection — never three stacked cards. -->
        @if (selection(); as kind) {
          <div class="tp-inspector" role="region" [attr.aria-label]="kindLabel()">

            <div class="tp-inspector-head">
              <span class="tp-inspector-icon" aria-hidden="true" [style.border-radius]="iconRadius()"
                    [style.background]="iconBackground()" [style.border]="'2px solid ' + iconBorder()"></span>
              <div class="tp-inspector-titles">
                <div class="tp-kind">{{ kindLabel() }}</div>
                @if (kind === 'background') {
                  <div class="tp-static-name">Bild</div>
                } @else {
                  <input class="cq-in" type="text" [value]="editableName()" (input)="renameSelection($event)"
                         [attr.aria-label]="kind === 'table' ? 'Tischnummer' : 'Name des Bereichs'"/>
                }
              </div>
              <button type="button" class="cq-btn tp-close" (click)="clearSelection()" aria-label="Auswahl aufheben">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
                     stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
              </button>
            </div>

            <div class="tp-inspector-body">
              @switch (kind) {
                @case ('table') {
                  <app-table-editor [table]="selectedTable" [areas]="areas" (tableChange)="applyTable($event)"/>
                }
                @case ('area') {
                  <app-area-editor [area]="selectedArea" (areaChange)="applyArea($event)"/>
                }
                @case ('background') {
                  <app-background-editor [background]="background"
                                         [renderedWidth]="renderedBackground?.width ?? 0"
                                         [busy]="uploadingBackground || saving"
                                         (placementChanged)="patchBackground($event)"
                                         (replaceRequested)="pickFile()"/>
                }
              }

              <div class="tp-inspector-actions">
                @if (kind !== 'background') {
                  <button type="button" class="cq-btn tp-dup" (click)="duplicateSelection()">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <rect x="8" y="8" width="12" height="12" rx="2.5"/><path d="M4 16V6a2 2 0 0 1 2-2h10"/>
                    </svg>
                    Duplizieren
                  </button>
                }
                <button type="button" class="cq-btn tp-del" (click)="deleteSelection()">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M4 7h16M9 7V4.5h6V7M6 7l1 13h10l1-13"/>
                  </svg>
                  Löschen
                </button>
              </div>
            </div>
          </div>
        }

        @if (loading) {
          <div class="tp-status" role="status">
            <div class="tp-status-card">
              <svg class="h-8 w-8 animate-spin" [style.color]="accent" fill="none" viewBox="0 0 24 24"
                   aria-hidden="true">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
              </svg>
              Grundriss wird geladen …
            </div>
          </div>
        } @else if (error) {
          <div class="tp-status" role="alert">
            <div class="tp-status-card">
              Der Grundriss konnte nicht geladen werden.
              <button type="button" class="cq-btn tp-save" (click)="load()">Erneut versuchen</button>
            </div>
          </div>
        }
      </div>
    </div>
  `
})
export class TableLayoutComponent implements OnInit {
  private readonly canvas = viewChild<FloorPlanCanvasComponent>('canvas');
  private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('file');
  private readonly destroyRef = inject(DestroyRef);

  protected loading = true;
  protected error = false;
  protected saving = false;
  protected uploadingBackground = false;
  /** Whether the draft diverges from what was last loaded. Kept for tests and for future unsaved-work guards. */
  protected dirty = false;

  /** The editable draft. Public for templates + tests. */
  tables: FloorTable[] = [];
  areas: FloorArea[] = [];
  /** The stored background's metadata, with any local placement edits applied. `null` when the shard has none. */
  background: FloorPlanBackground | null = null;
  /** The decoded image's intrinsic size — the only source of the aspect ratio, which is never stored. */
  backgroundNaturalSize: {width: number; height: number} | null = null;
  /** Object URL of the fetched bytes; revoked whenever it is replaced (see {@link setObjectUrl}). */
  private backgroundObjectUrl: string | null = null;
  /** Whether the background's placement diverges from what was last loaded. */
  private backgroundDirty = false;

  selectedTableId: string | null = null;
  selectedAreaId: string | null = null;
  /** Whether the background is the current selection (which shows its move/resize frame on the canvas). */
  backgroundSelected = false;

  protected mode: FloorPlanMode = 'select';
  /** Vertices collected while drawing a new area. */
  draftNodes: AreaNode[] = [];

  protected gridVisible = true;
  protected snapEnabled = true;
  protected minorGridCm = 50;

  /** Persisted entities the admin removed; flushed on save (a draft entity is simply dropped). */
  private deletedTableIds: string[] = [];
  private deletedAreaIds: string[] = [];

  protected readonly minAreaNodes = MIN_AREA_NODES;
  protected readonly gridSteps = GRID_STEPS;
  protected readonly accent = EDITOR_ACCENT;
  protected readonly accept = BACKGROUND_ACCEPT;
  protected readonly TableShape = TableShape;

  protected readonly tools: readonly {mode: FloorPlanMode; label: string}[] = [
    {mode: 'select', label: 'Auswählen'},
    {mode: 'origin', label: 'Ursprung'},
    {mode: 'draw', label: 'Bereich'}
  ];

  protected readonly shapes: readonly {value: TableShape; label: string}[] = [
    {value: TableShape.Circle, label: 'Rund'},
    {value: TableShape.Rectangle, label: 'Quadrat'}
  ];

  constructor(private http: TableLayoutHttpService, private alert: GlobalAlertService) {
    // An object URL is a document-lifetime reference; leaving it behind leaks the whole image.
    this.destroyRef.onDestroy(() => this.setObjectUrl(null));
  }

  ngOnInit(): void {
    this.load();
  }

  get selectedTable(): FloorTable | null {
    return this.tables.find(t => t.id === this.selectedTableId) ?? null;
  }

  get selectedArea(): FloorArea | null {
    return this.areas.find(a => a.id === this.selectedAreaId) ?? null;
  }

  // region Inspector chrome

  /** What the single inspector is describing, or `null` when it is not shown at all. */
  selection(): Selection {
    if (this.selectedTable) return 'table';
    if (this.selectedArea) return 'area';
    if (this.backgroundSelected && this.background) return 'background';
    return null;
  }

  /** The inspector's small uppercase kind line. */
  protected kindLabel(): string {
    switch (this.selection()) {
      case 'table':
        return this.selectedTable?.tableShape === TableShape.Circle ? 'Runder Tisch' : 'Quadratischer Tisch';
      case 'area':
        return 'Bereich (Polygon)';
      case 'background':
        return 'Hintergrundbild';
      default:
        return '';
    }
  }

  /** The name shown in the inspector's editable title field. */
  protected editableName(): string {
    return this.selection() === 'table' ? this.selectedTable?.name ?? '' : this.selectedArea?.name ?? '';
  }

  /** The inspector's 34 px identity chip mirrors the selected object: a table's shape, an area's colour. */
  protected iconRadius(): string {
    return this.selection() === 'table' && this.selectedTable?.tableShape === TableShape.Circle ? '999px' : '9px';
  }

  protected iconBackground(): string {
    switch (this.selection()) {
      case 'area':
        return withAlpha(this.selectedArea?.color ?? EDITOR_ACCENT, 0.35);
      case 'background':
        return '#eef4f1';
      default:
        return '#fdf1f7';
    }
  }

  protected iconBorder(): string {
    switch (this.selection()) {
      case 'area':
        return this.selectedArea?.color ?? EDITOR_ACCENT;
      case 'background':
        return '#9ab0c8';
      default:
        return EDITOR_ACCENT;
    }
  }

  /** The inspector's title field renames whichever of the two nameable objects is selected. */
  renameSelection(event: Event): void {
    const name = (event.target as HTMLInputElement).value;
    const table = this.selectedTable;
    if (table) {
      this.applyTable({...table, name});
      return;
    }
    const area = this.selectedArea;
    if (area) this.applyArea({...area, name});
  }

  /**
   * Copies the selection, nudged by {@link DUPLICATE_OFFSET} so the copy is visible rather than exactly on top of
   * its original (the handover's *Duplizieren*), and selects the copy so it can be dragged away immediately.
   */
  duplicateSelection(): void {
    const table = this.selectedTable;
    if (table) {
      const copy: FloorTable = {
        ...table,
        id: newDraftId(),
        name: nextTableName(this.tables),
        x: table.x + DUPLICATE_OFFSET,
        y: table.y + DUPLICATE_OFFSET
      };
      this.tables = this.resolvedTables([...this.tables, copy], this.areas);
      this.selectedTableId = copy.id;
      this.markDirty();
      return;
    }

    const area = this.selectedArea;
    if (!area) return;
    const nodes = area.nodes.map(node => ({x: node.x + DUPLICATE_OFFSET, y: node.y + DUPLICATE_OFFSET}));
    const centre = polygonCentroid(nodes);
    const copy: FloorArea = {
      ...area,
      id: newDraftId(),
      // Area names are unique per shard (enforced by `AreaService`), so a copy cannot reuse the original's.
      name: `${area.name} Kopie`,
      nodes,
      centerX: centre.x,
      centerY: centre.y,
      // Offset explicitly rather than inherited through the spread: a duplicate is a pure translation, so the
      // copy's anchor is the original's shifted by the same amount — leaving the spread's value would park the
      // caption over the original shape.
      labelX: area.labelX + DUPLICATE_OFFSET,
      labelY: area.labelY + DUPLICATE_OFFSET
    };
    this.areas = [...this.areas, copy];
    this.tables = this.resolvedTables(this.tables, this.areas);
    this.selectedAreaId = copy.id;
    this.markDirty();
  }

  // endregion

  /**
   * The background as the canvas needs it: stored placement plus the render size, where the width resolves the
   * "not placed yet" (`width === 0`) case against the image's natural width and the height is always derived from
   * the aspect ratio.
   */
  get renderedBackground(): RenderedBackground | null {
    const background = this.background;
    const url = this.backgroundObjectUrl;
    const natural = this.backgroundNaturalSize;
    if (!background || !url || !natural) return null;

    const width = backgroundWidthOrNatural(background.width, natural.width);
    return {
      objectUrl: url,
      x: background.x,
      y: background.y,
      width,
      height: backgroundHeight(width, natural.width, natural.height),
      opacity: background.opacity
    };
  }

  /** Display name of an area id. */
  areaName(areaId: string | null): string {
    if (!areaId) return 'Kein Bereich';
    return this.areas.find(a => a.id === areaId)?.name ?? 'Kein Bereich';
  }

  /** How many drafted tables sit in an area. */
  tableCount(areaId: string): number {
    return this.tables.filter(t => t.areaId === areaId).length;
  }

  load(): void {
    this.loading = true;
    this.error = false;
    forkJoin({
      tables: this.http.listTables(),
      areas: this.http.listAreas(),
      background: this.http.getBackground()
    }).subscribe({
      next: ({tables, areas, background}) => {
        this.areas = areas;
        // The stored areaId is re-derived from the geometry on load too: an area reshaped in a previous session may
        // have moved out from under a table, and the plan on screen must not disagree with the ids it will save.
        this.tables = this.resolvedTables(tables, areas);
        this.deletedTableIds = [];
        this.deletedAreaIds = [];
        this.draftNodes = [];
        this.mode = 'select';
        this.dirty = false;
        this.backgroundDirty = false;
        this.loading = false;
        // A selection that no longer exists (deleted elsewhere, or renumbered by a save) must not linger.
        if (!this.selectedTable) this.selectedTableId = null;
        if (!this.selectedArea) this.selectedAreaId = null;

        this.applyBackground(background);
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  // region Tables

  /**
   * Adds a table to the draft at the current view's centre in the chosen shape, at the default footprint and
   * numbered with the lowest free number. It carries a draft id until the first successful save (`toTableUpdate`
   * drops it so the upsert inserts).
   */
  addTable(shape: TableShape = TableShape.Rectangle): void {
    const centre = this.viewCentre();
    const table: FloorTable = {
      id: newDraftId(),
      name: nextTableName(this.tables),
      tableType: 0,
      tableShape: shape,
      seats: 4,
      x: Math.round(centre.x - DEFAULT_TABLE_SIZE / 2),
      y: Math.round(centre.y - DEFAULT_TABLE_SIZE / 2),
      width: DEFAULT_TABLE_SIZE,
      height: DEFAULT_TABLE_SIZE,
      description: '',
      temporary: false,
      areaId: null,
      status: TableStatus.Free,
      idleMinutes: null
    };
    this.tables = this.resolvedTables([...this.tables, table], this.areas);
    this.selectedTableId = table.id;
    this.selectedAreaId = null;
    this.backgroundSelected = false;
    this.mode = 'select';
    this.markDirty();
  }

  selectTable(id: string): void {
    this.selectedTableId = id;
    this.selectedAreaId = null;
    this.backgroundSelected = false;
  }

  /** Replaces a table in the draft with the patched copy emitted by the editor/canvas. */
  applyTable(table: FloorTable): void {
    this.tables = this.resolvedTables(
      this.tables.map(t => t.id === table.id ? table : t), this.areas);
    this.markDirty();
  }

  moveTable(change: {id: string; x: number; y: number}): void {
    this.tables = this.resolvedTables(
      this.tables.map(t => t.id === change.id ? {...t, x: change.x, y: change.y} : t), this.areas);
    this.markDirty();
  }

  resizeTable(change: {id: string; width: number; height: number}): void {
    // Resizing moves the centre, so the area match can change here too.
    this.tables = this.resolvedTables(
      this.tables.map(t => t.id === change.id ? {...t, width: change.width, height: change.height} : t), this.areas);
    this.markDirty();
  }

  /**
   * Removes a table from the draft. A persisted one is remembered so the save can delete it server-side; a draft
   * one has no server counterpart and is simply forgotten.
   *
   * The confirmation is deliberately kept even though the handover has none — its own notes list a PIN step-up on
   * delete as an open item, and shipping a one-click destructive action in the meantime is not the intent.
   */
  deleteTable(id: string): void {
    const table = this.tables.find(t => t.id === id);
    if (!table) return;
    if (!confirm(`Tisch ${table.name} wirklich löschen?`)) return;

    if (!isDraftId(id)) this.deletedTableIds = [...this.deletedTableIds, id];
    this.tables = this.tables.filter(t => t.id !== id);
    if (this.selectedTableId === id) this.selectedTableId = null;
    this.markDirty();
  }

  // endregion

  // region Areas

  setMode(mode: FloorPlanMode): void {
    this.mode = mode;
    this.draftNodes = [];
    if (mode !== 'select') {
      this.selectedTableId = null;
      this.selectedAreaId = null;
      this.backgroundSelected = false;
    }
  }

  startArea(): void {
    this.setMode('draw');
  }

  cancelArea(): void {
    this.mode = 'select';
    this.draftNodes = [];
  }

  addDraftNode(node: AreaNode): void {
    this.draftNodes = [...this.draftNodes, node];
  }

  /** Turns the collected vertices into a draft area. The polygon is implicitly closed, so nothing is repeated. */
  finishArea(): void {
    if (this.draftNodes.length < MIN_AREA_NODES) return;
    const nodes = this.draftNodes.map(node => ({...node}));
    const centre = polygonCentroid(nodes);
    const label = polygonLabelAnchor(nodes);
    const area: FloorArea = {
      id: newDraftId(),
      name: `Bereich ${this.areas.length + 1}`,
      nodes,
      color: NEW_AREA_COLOR,
      // Both derived points are filled in locally so the marker and the caption render immediately; the server
      // recomputes them on save (with the identical formulas, so the values do not move on the round-trip).
      centerX: centre.x,
      centerY: centre.y,
      labelX: label.x,
      labelY: label.y
    };
    this.areas = [...this.areas, area];
    // A newly drawn area may already enclose existing tables.
    this.tables = this.resolvedTables(this.tables, this.areas);
    this.selectedAreaId = area.id;
    this.selectedTableId = null;
    this.draftNodes = [];
    this.mode = 'select';
    this.markDirty();
  }

  selectArea(id: string): void {
    this.selectedAreaId = id;
    this.selectedTableId = null;
    this.backgroundSelected = false;
  }

  applyArea(area: FloorArea): void {
    this.replaceArea(area.id, () => area);
  }

  /** An area dragged as a whole: the canvas sends the complete, already-snapped node list. */
  moveArea(change: {areaId: string; nodes: AreaNode[]}): void {
    this.replaceArea(change.areaId, area => ({...area, nodes: change.nodes}));
  }

  moveAreaNode(change: {areaId: string; index: number; x: number; y: number}): void {
    this.replaceArea(change.areaId, area => ({
      ...area,
      nodes: area.nodes.map((node, i) => i === change.index ? {x: change.x, y: change.y} : node)
    }));
  }

  /** Inserts a vertex **after** `index`, which is where the canvas continues its drag. */
  addAreaNode(change: {areaId: string; index: number; x: number; y: number}): void {
    this.replaceArea(change.areaId, area => ({
      ...area,
      nodes: [
        ...area.nodes.slice(0, change.index + 1),
        {x: change.x, y: change.y},
        ...area.nodes.slice(change.index + 1)
      ]
    }));
  }

  /** Removes a vertex, refusing to drop below the minimum that still forms a polygon. */
  removeAreaNode(change: {areaId: string; index: number}): void {
    const area = this.areas.find(a => a.id === change.areaId);
    if (!area || area.nodes.length <= MIN_AREA_NODES) return;
    this.replaceArea(change.areaId, current => ({
      ...current,
      nodes: current.nodes.filter((_, i) => i !== change.index)
    }));
  }

  /**
   * Removes an area from the draft and un-assigns the tables that pointed at it. The backend keeps a deleted
   * area's id on its tables (`AreaId` has no foreign key), so clearing it here is what stops the table list from
   * showing a dangling reference.
   */
  deleteArea(id: string): void {
    const area = this.areas.find(a => a.id === id);
    if (!area) return;
    if (!confirm(`Bereich ${area.name} wirklich löschen? Zugeordnete Tische bleiben bestehen.`)) return;

    if (!isDraftId(id)) this.deletedAreaIds = [...this.deletedAreaIds, id];
    this.areas = this.areas.filter(a => a.id !== id);
    // Re-resolve rather than only clearing this id: a table inside a *deleted* area may now fall into an
    // overlapping one that was previously shadowed by it.
    this.tables = this.resolvedTables(this.tables.map(t => t.areaId === id ? {...t, areaId: null} : t), this.areas);
    if (this.selectedAreaId === id) this.selectedAreaId = null;
    this.markDirty();
  }

  /** Applies a change to one area and re-resolves every table's area, since the outline may have moved. */
  private replaceArea(id: string, change: (area: FloorArea) => FloorArea): void {
    const areas = this.areas.map(area => area.id === id ? change(area) : area);
    this.areas = areas;
    this.tables = this.resolvedTables(this.tables, areas);
    this.markDirty();
  }

  // endregion

  // region Selection & keyboard

  clearSelection(): void {
    this.selectedTableId = null;
    this.selectedAreaId = null;
    this.backgroundSelected = false;
  }

  /** Delete/Backspace on the canvas, or the inspector's Löschen: removes whatever is selected. */
  deleteSelection(): void {
    if (this.selectedTableId) this.deleteTable(this.selectedTableId);
    else if (this.selectedAreaId) this.deleteArea(this.selectedAreaId);
    else if (this.backgroundSelected) this.deleteBackground();
  }

  // endregion

  // region Move origin

  /**
   * Re-bases the whole plan so the picked point becomes `(0, 0)` (the handover's §7), then flushes it.
   *
   * The view is compensated by exactly the shift applied to the geometry, so **nothing moves on screen** — only the
   * numbers change. The draft is re-based atomically client-side first and only then saved, which is what keeps a
   * failed save from leaving a half-shifted plan: on failure the whole shift is still in the (dirty) draft and
   * Speichern retries it.
   */
  moveOrigin(origin: AreaNode): void {
    this.mode = 'select';
    // Picking the origin where it already is would be a no-op save.
    if (origin.x === 0 && origin.y === 0) return;

    const canvas = this.canvas();
    const view = canvas?.currentView() ?? {x: 0, y: 0, scale: 1};
    const rebased = rebaseOrigin(origin, {tables: this.tables, areas: this.areas, background: this.background}, view);

    this.tables = this.resolvedTables(rebased.tables, rebased.areas);
    this.areas = rebased.areas;
    if (rebased.background) {
      this.background = rebased.background;
      this.backgroundDirty = true;
    }
    canvas?.applyView(rebased.view);
    this.markDirty();

    this.flush('Der Ursprung wurde verschoben und der Grundriss gespeichert.',
      'Der Ursprung wurde verschoben, konnte aber nicht gespeichert werden. Die Änderung ist noch nicht gesichert — bitte erneut speichern.');
  }

  /**
   * The world point at the middle of the current view — where a new table is placed, so it appears where the admin
   * is looking. Falls back to the origin before the canvas has been laid out (or in a test without one).
   */
  private viewCentre(): AreaNode {
    return this.canvas()?.viewCenterWorld() ?? {x: 0, y: 0};
  }

  // endregion

  // region Background image

  /** Adopts a freshly loaded/stored background: fetches its bytes and decodes its intrinsic size. */
  private applyBackground(background: FloorPlanBackground | null): void {
    this.background = background;
    if (!background) {
      this.setObjectUrl(null);
      this.backgroundNaturalSize = null;
      this.backgroundSelected = false;
      return;
    }

    // `/booking/FloorPlan/Image` is authenticated, so the bytes must come through the HTTP client and become an
    // object URL — pointing an <img>/CSS url() at the route directly would fire an unauthenticated request.
    this.http.downloadBackgroundImage().subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        this.setObjectUrl(url);
        this.measureImage(url);
      },
      error: () => {
        this.setObjectUrl(null);
        this.backgroundNaturalSize = null;
        this.alert.createAndShow('Hintergrundbild',
          'Das Hintergrundbild konnte nicht geladen werden.', AlertLevel.warning, 3000);
      }
    });
  }

  /**
   * Decodes the image to learn its aspect ratio — the one thing the backend deliberately does not store, because
   * storing it is what would let a width/height pair drift out of proportion.
   */
  private measureImage(url: string): void {
    const image = new Image();
    image.onload = () => {
      // An SVG without an intrinsic size reports 0; fall back to a square so the frame is at least usable.
      const width = image.naturalWidth || 1000;
      const height = image.naturalHeight || width;
      this.backgroundNaturalSize = {width, height};
    };
    image.onerror = () => {
      this.backgroundNaturalSize = null;
    };
    image.src = url;
  }

  /** Swaps the object URL, revoking the previous one so the decoded image is actually released. */
  private setObjectUrl(url: string | null): void {
    if (this.backgroundObjectUrl) URL.revokeObjectURL(this.backgroundObjectUrl);
    this.backgroundObjectUrl = url;
  }

  selectBackground(): void {
    if (!this.background) return;
    this.backgroundSelected = true;
    this.selectedTableId = null;
    this.selectedAreaId = null;
    this.mode = 'select';
  }

  /**
   * The toolbar's *Bild* chip: it selects the stored image on the plan, or — when there is none yet — opens the
   * file picker, exactly as the handover's `onBgButton`.
   */
  onBackgroundChip(): void {
    if (this.background) this.selectBackground();
    else this.pickFile();
  }

  /** Opens the OS file picker (shared by the toolbar chip and the inspector's *Bild ersetzen*). */
  pickFile(): void {
    this.fileInput().nativeElement.click();
  }

  /**
   * Validates the picked file against the same rules the server enforces, so a 40 MB scan or a `.gif` fails
   * instantly with a readable message instead of after a long upload the server rejects anyway.
   */
  onFilePicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Always clear the input: re-picking the SAME file must fire `change` again (a fixed corrected export is the
    // normal case here), and the browser suppresses that while the value is unchanged.
    input.value = '';
    if (!file) return;

    const problem = validateBackgroundFile(file);
    if (problem) {
      this.alert.createAndShow('Hintergrundbild', problem, AlertLevel.error, 4000);
      return;
    }
    this.uploadBackground(file);
  }

  /**
   * Uploads a new (or replacement) image.
   *
   * The placement is **not** sent, which is exactly what makes a replacement inherit the previous image's position,
   * width and opacity — re-exporting a corrected plan must not force a re-align.
   */
  uploadBackground(file: File): void {
    if (this.uploadingBackground) return;
    this.uploadingBackground = true;
    this.http.uploadBackground(file).subscribe({
      next: background => {
        this.uploadingBackground = false;
        this.backgroundDirty = false;
        // A new file means a new aspect ratio, so the decoded size has to be discarded before the fetch.
        this.backgroundNaturalSize = null;
        this.applyBackground(background);
        this.backgroundSelected = true;
        this.selectedTableId = null;
        this.selectedAreaId = null;
        this.alert.createAndShow('Hintergrundbild', 'Das Bild wurde hochgeladen.', AlertLevel.success, 2000);
      },
      error: (err: {status?: number}) => {
        this.uploadingBackground = false;
        this.alert.createAndShow('Fehler',
          err?.status === 403 ? 'Keine Berechtigung für diese Aktion.'
            : err?.status === 400 ? 'Das Bild wurde abgelehnt (Format oder Größe).'
              : 'Das Bild konnte nicht hochgeladen werden.',
          AlertLevel.error, 3000);
      }
    });
  }

  /** Applies a placement edit locally; it is pushed with the next Speichern. */
  patchBackground(change: {x?: number; y?: number; width?: number; opacity?: number}): void {
    if (!this.background) return;
    this.background = {...this.background, ...change};
    this.backgroundDirty = true;
    this.markDirty();
  }

  moveBackground(change: {x: number; y: number}): void {
    this.patchBackground(change);
  }

  /** Aspect-locked: only the width is stored, so only the width is patched. */
  resizeBackground(change: {width: number}): void {
    this.patchBackground({width: Math.round(change.width)});
  }

  deleteBackground(): void {
    if (!this.background) return;
    if (!confirm('Hintergrundbild wirklich löschen?')) return;

    this.http.deleteBackground().subscribe({
      next: () => {
        this.backgroundDirty = false;
        this.applyBackground(null);
        this.alert.createAndShow('Hintergrundbild', 'Das Bild wurde gelöscht.', AlertLevel.success, 2000);
      },
      error: () => this.alert.createAndShow('Fehler',
        'Das Hintergrundbild konnte nicht gelöscht werden.', AlertLevel.error, 3000)
    });
  }

  // endregion

  private markDirty(): void {
    this.dirty = true;
  }

  /**
   * Re-resolves every table's `areaId` from the geometry: the area whose polygon contains the table's centre, last
   * one winning on overlap.
   *
   * This is the whole of the auto-matching contract (the handover's §6). It runs on every emitted move — i.e. per
   * pixel of a drag — which is what makes the inspector's *Bereich* row live and guarantees the id that gets saved
   * is the one the admin saw.
   */
  private resolvedTables(tables: readonly FloorTable[], areas: readonly FloorArea[]): FloorTable[] {
    return tables.map(table => {
      const areaId = areaOfTable(areas, table)?.id ?? null;
      return table.areaId === areaId ? table : {...table, areaId};
    });
  }

  /**
   * Flushes the whole draft: pending deletes first (so a name freed by a delete can be reused in the same save),
   * then the areas, then the tables. Areas go first because a table may point at an area that only exists in the
   * draft — the stored areas come back with their real ids, which are mapped onto the tables by **name** (unique
   * per shard, enforced by `AreaService`) before the table batch is sent. The background's placement goes last: it
   * is an independent single row, so it cannot invalidate anything the earlier steps wrote.
   */
  save(): void {
    this.flush('Der Grundriss wurde gespeichert.', 'Der Grundriss konnte nicht gespeichert werden.');
  }

  private flush(successMessage: string, failureMessage: string): void {
    if (this.saving) return;
    this.saving = true;

    const deletes: Observable<unknown>[] = [
      ...this.deletedTableIds.map(id => this.http.deleteTable(id)),
      ...this.deletedAreaIds.map(id => this.http.deleteArea(id))
    ];

    // `forkJoin([])` never emits, so an empty delete set has to short-circuit to a single-value observable.
    const prelude: Observable<unknown> = deletes.length > 0 ? forkJoin(deletes) : of(null);

    prelude.pipe(
      switchMap(() => this.http.upsertAreas(this.areas.map(toAreaUpdate))),
      switchMap(storedAreas => this.http.upsertTables(this.resolveAreaIds(storedAreas).map(toTableUpdate))),
      switchMap(() => this.saveBackgroundPlacement())
    ).subscribe({
      next: () => {
        this.saving = false;
        this.alert.createAndShow('Gespeichert', successMessage, AlertLevel.success, 2000);
        this.load();
      },
      error: (err: {status?: number}) => {
        this.saving = false;
        this.alert.createAndShow('Fehler',
          err?.status === 403 ? 'Keine Berechtigung für diese Aktion.'
            : err?.status === 409 ? 'Ein Bereichsname ist bereits vergeben.'
              : failureMessage,
          AlertLevel.error, 3000);
      }
    });
  }

  /**
   * Pushes the background's placement — only when there is one and it actually changed.
   *
   * **A stored `width` of 0 must not be replayed.** It is the read-time "not placed yet, draw it at its natural
   * size" signal; the *update* endpoint requires a positive width and answers `0` with a 400. So merely moving a
   * never-resized image (or dialling its opacity) would otherwise lose the whole placement — silently, because the
   * tables and areas earlier in the same flush succeed first. The width actually on screen is sent instead, which
   * is also the truthful answer: once the admin has positioned it, it IS placed.
   *
   * If neither a stored nor a rendered width is available (the bytes are still in flight, or the image failed to
   * decode) the push is skipped rather than sent with a zero the server would reject.
   */
  private saveBackgroundPlacement(): Observable<unknown> {
    const background = this.background;
    if (!background || !this.backgroundDirty) return of(null);

    const width = background.width > 0 ? background.width : this.renderedBackground?.width ?? 0;
    if (!(width > 0)) return of(null);

    return this.http.updateBackgroundPlacement({
      x: background.x,
      y: background.y,
      width,
      opacity: background.opacity
    });
  }

  /** Rewrites draft area ids on the tables to the ids the backend just assigned (matched by area name). */
  private resolveAreaIds(storedAreas: readonly FloorArea[]): FloorTable[] {
    const idByName = new Map(storedAreas.map(area => [area.name, area.id]));
    const draftIdToName = new Map(this.areas.filter(a => isDraftId(a.id)).map(a => [a.id, a.name]));

    return this.tables.map(table => {
      if (!table.areaId || !isDraftId(table.areaId)) return table;
      const name = draftIdToName.get(table.areaId);
      // A table pointing at an area that vanished before the save loses the assignment rather than 400-ing.
      return {...table, areaId: (name !== undefined ? idByName.get(name) : undefined) ?? null};
    });
  }
}
